import classnames from "classnames";
import React, { MouseEvent, MouseEventHandler, useRef, useState } from "react";
import { Badge, Button } from "reactstrap";

import { ProductTileModel } from "@api/mappers/products.mapper";

import { useOutsideAlerter } from "@utils/hooks/useOutsideAlerter";
import { useScrollLock } from "@utils/hooks/useScrollLock";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { ProductTileLayout } from "@components/Product/ProductTile/productTile.config";

import { ProductTileRenditions } from "../ProductImage/mappedRenditions";
import { ProductTile } from "../ProductTile/productTile.container";
import "./productComparisonBar.scss";

type ProductComparisonBarProps = {
    products: Array<ProductTileModel>;
    onRemoveProduct: (sku: string) => void;
    onEmptySkuList: MouseEventHandler<HTMLButtonElement>;
    comparePagePath: string;
    stickyOrderButton: boolean;
};

export const PRODUCT_COMPARISON_BAR_SCROLL_LOCK = "product-comparison-bar";

const ProductComparisonBar = ({
    products,
    onRemoveProduct,
    comparePagePath,
    onEmptySkuList,
    stickyOrderButton,
}: ProductComparisonBarProps) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [showProductList, setShowProductList] = useState(false);
    const { t } = useGranite();
    const { toggle: toggleScrollLock, unlock } = useScrollLock(
        PRODUCT_COMPARISON_BAR_SCROLL_LOCK,
    );

    const createOnRemoveProductClickHandler =
        (sku: string) => (event: MouseEvent) => {
            event.preventDefault();
            onRemoveProduct(sku);
        };

    useOutsideAlerter(() => {
        setShowProductList(false);
        unlock();
    }, ref);

    const handleOnOpenButtonClick = () => {
        setShowProductList(show => !show);
        toggleScrollLock();
    };

    return (
        <div
            className={classnames("product-comparison-bar__container", {
                "product-comparison-bar__container--open": showProductList,
                "product-comparison-bar__container--sticky-order-button":
                    stickyOrderButton,
            })}
        >
            <div
                className={classnames("modal-backdrop fade layer-comparison", {
                    show: showProductList,
                })}
            ></div>
            <div
                ref={ref}
                className={classnames("product-comparison-bar", {
                    "product-comparison-bar--open": showProductList,
                })}
            >
                <div className="product-comparison-bar__button">
                    <Button
                        type="button"
                        size="md"
                        color="primary"
                        onClick={handleOnOpenButtonClick}
                    >
                        <i
                            className={classnames("las", "mr-2", {
                                "la-angle-down": showProductList,
                                "la-angle-up": !showProductList,
                            })}
                        />
                        <span>
                            <span
                                className={
                                    stickyOrderButton
                                        ? "d-none d-md-inline"
                                        : "d-inline"
                                }
                            >
                                {t("productComparison.compare")}
                            </span>
                            <span
                                className={
                                    stickyOrderButton
                                        ? "d-inline d-md-none"
                                        : "d-none"
                                }
                            >
                                {t("productComparison.compare.short")}
                            </span>
                        </span>
                        <Badge
                            color="secondary"
                            className="product-comparison-bar__button__badge ml-2"
                        >
                            {products?.length || 0}/6
                        </Badge>
                    </Button>
                </div>
                {showProductList && (
                    <div className="product-comparison-bar__products">
                        <Button
                            color="link"
                            type="button"
                            onClick={e => {
                                unlock();
                                onEmptySkuList(e);
                            }}
                        >
                            {t("productComparison.removeAll")}
                        </Button>
                        <div className="product-comparison-bar__products__wrapper">
                            {products.map((product, index) => (
                                <div
                                    className={
                                        "product-comparison-bar__product mb-3"
                                    }
                                    key={product.sku}
                                >
                                    <ProductTile
                                        sku={product.sku}
                                        product={product}
                                        allowInstantAddToCart={false}
                                        lazyLoadImage={false}
                                        productTileConfig={{
                                            border: true,
                                            amountOfFeatures: 0,
                                            imageRenditions:
                                                ProductTileRenditions,
                                            horizontal: true,
                                            components:
                                                ProductTileLayout.COMPARISON_BAR_HORIZONTAL(),
                                            columnFractions: [5, 7],
                                        }}
                                        analyticsContext={{
                                            list: "product-comparison",
                                            placement: index + 1,
                                        }}
                                    >
                                        <Button
                                            onClick={createOnRemoveProductClickHandler(
                                                product.sku,
                                            )}
                                            className="product-comparison-bar__product__button position-absolute ml-auto"
                                            color="transparent"
                                            type="button"
                                            aria-label={t(
                                                "accessibility.comparison.remove",
                                            )}
                                        >
                                            <span className="las la-times"></span>
                                        </Button>
                                    </ProductTile>
                                </div>
                            ))}
                        </div>
                        <a
                            className="product-comparison-bar__compareProducts btn btn-primary btn-md d-block"
                            href={`${comparePagePath}?products=${products
                                .map(product => product.sku)
                                .join(",")}`}
                        >
                            {t(
                                "productComparison.compareProducts",
                                products.length,
                            )}
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};

export { ProductComparisonBar, ProductComparisonBarProps };
